// ABOUTME: SPEC-57 — the view-toggle's presentation: a "Views" pill that expands into the chapter row.
// ABOUTME: Desktop is bottom-anchored (424:9/424:173); mobile is top-left anchored (427:23/166). Pure props.

import React from 'react';
import { RAIL_UNIT, u, uMin } from '../railGeometry';
import { MOBILE_UNIT, mu, muMin } from '../mobileGeometry';
import { BAR_H_PCT, BAR_BOTTOM_PCT } from '../../Projectionist/overlays/CaptionOverlay';
import { useFadePresence, FADE_MS } from '../../Projectionist/overlays/useFadePresence';
import { useControlsLift, useLiftTransition } from '../../../uiExperiment/bottomChromeStore'; // SPEC-82 M1
import { BUTTON, DESKTOP, MOBILE, MIN_LABEL_PX, PILL_LABEL } from './viewToggleGeometry';

// SPEC-57 fix 4 (owner report 2026-09-11, iterated twice the same day): the pill must never move.
// Desktop: the row sits BESIDE the pill (`flex-row`, `items-end` bottom-aligning both to the
// container's pinned bottom edge) — the owner asked for this explicitly, and desktop has the width
// to wrap onto further lines without hitting anything. Mobile: a horizontal arrangement at the
// pill's authored y (the OPHION wordmark's own row, D-4) collided with the centred wordmark and the
// hamburger menu the instant the row wrapped past ~2 buttons wide — a hard width constraint, not a
// preference — so mobile keeps the vertical column (`flex-col`, `items-start` top-aligning both to
// the pinned top edge) it already had; that arrangement already satisfied "the pill never moves"
// (D-4's OQ-1 fix) without this collision. Both directions pin the pill to the SAME anchored edge
// the container itself is fixed to; only the axis differs per composition's available space.
const TWEEN_OFFSET_AUTHORED = 12;

// D-4: the desktop column's bottom edge sits one gutter above the CC bar's TOP — composed off the
// bar's own published percentages (CaptionOverlay.js) rather than a value copied out of them, so a
// retune of the bar there is not a silent drift here. Desktop is unaffected by the mobile-frame fix.
// SPEC-82 review M1: `lift` is the voice dock's revealed controls row (0 while it is closed / without control_reveal, so the
// flag-off arithmetic is unchanged); the row sits on the bar's top edge, so the pill + row rise by its height instead of under it.
export const desktopBottom = (lift = 0) => `calc(${BAR_BOTTOM_PCT}% + ${BAR_H_PCT}% + ${u(DESKTOP.gutterAboveBar + lift)})`;

// D-4 (mobile, re-decided): 427:23/166's pill sits at the SAME authored y (112, the OPHION
// wordmark's own row) whether the column is open or closed — growth is downward into the column,
// never a bottom gutter, so there is nothing to derive per-state (OQ-1). `hasTags` (D-6/OQ-4,
// useReelViewToggle.js, unchanged) is no longer consumed here: a top-anchored column cannot collide
// with the bottom-anchored tag row/CC bar (viewToggleGeometry.js's MOBILE comment has the arithmetic).
const mobileTop = (shift = 0) => mu(MOBILE.top + shift); // SPEC-82: `shift` is 0 unless collection_menu shares this corner

/** One chapter/collection button. `unit`/`unitMin` are the composition's own `u`/`uMin` pair. */
function ViewButton({ button, unit, unitMin, rowGapAuthored }) {
  return (
    <button
      type="button"
      data-testid={`view-toggle-button-${button.key}`}
      aria-current={button.selected ? 'true' : undefined}
      disabled={button.disabled}
      onClick={button.onPress}
      className="reel-touch-target relative bg-reel-chrome-panel backdrop-blur-reel-chrome font-reel-medium text-reel-chrome-textPrimary whitespace-nowrap disabled:cursor-default"
      style={{
        height: unit(BUTTON.h),
        padding: `0 ${unit(BUTTON.padX)}`,
        borderRadius: unit(BUTTON.radius),
        fontSize: unitMin(BUTTON.font, MIN_LABEL_PX),
        lineHeight: BUTTON.lineHeight,
        opacity: button.selected ? 1 : BUTTON.dimOpacity, // the whole highlight treatment (§1.2)
        // OverlayPortal disables pointer events with an inline style (ReelFooter.js:117-120); a
        // Tailwind class cannot win against it.
        pointerEvents: 'auto',
        // index.css `.reel-touch-target::after` — capped at the row's own pitch so two rows on a
        // tight mobile stack tile rather than overlapping (the defect index.css:114-119 exists for).
        '--hit-h': unit(BUTTON.h + rowGapAuthored),
      }}
    >
      {button.label}
    </button>
  );
}

/**
 * @param {{ composition: 'desktop'|'mobile', open: boolean, onToggleOpen: Function,
 *           buttons: Array }} props
 */
function ReelViewToggle({ composition, open, onToggleOpen, buttons, topShift = 0 }) {
  const mobile = composition === 'mobile';
  const unit = mobile ? mu : u;
  const unitMin = mobile ? muMin : uMin;
  const rowGapAuthored = mobile ? MOBILE.gapY : DESKTOP.gapY;
  // D-8: fade/tween on the existing chrome timing constant, never a hard show/hide (R4).
  const { present, opacity } = useFadePresence(open, FADE_MS);
  const lift = useControlsLift('desktop'); // SPEC-82 M1: 0 on mobile (top-anchored) and whenever the controls row is closed
  const liftTransition = useLiftTransition('bottom');

  return (
    <div
      data-testid="view-toggle-container"
      // SPEC-57 fix 4: desktop's row sits BESIDE the pill (`flex-row`, `items-end` bottom-aligns
      // both to the pinned bottom edge). Mobile keeps the vertical column (`flex-col`, `items-start`
      // top-aligns both to the pinned top edge) — a horizontal row at the wordmark's y collides with
      // it and the hamburger menu on a narrow screen (measured, not a style choice).
      className={`fixed flex ${mobile ? 'flex-col items-start' : 'flex-row items-end'}`}
      style={{
        '--u': mobile ? MOBILE_UNIT : RAIL_UNIT,
        left: mobile ? unit(MOBILE.leftInset) : `${DESKTOP.leftPct}%`,
        top: mobile ? mobileTop(topShift) : undefined,
        bottom: mobile ? undefined : desktopBottom(lift),
        ...(!mobile && liftTransition ? { transition: liftTransition } : {}),
        gap: unit(rowGapAuthored),
        // SPEC-57 fix 4/7 (owner report 2026-09-11): DESKTOP.maxWidthPct (~55.7%) was authored for
        // the ORIGINAL vertical stack, where the row had to fit inside the CC bar's own width
        // because it sat directly above it. Beside the pill, that constraint has no reason to
        // exist — below the --u floor (2064px, feedback-preserve-laptop-visuals) button/gap sizes
        // are CONSTANT real px while a percentage-of-3840-canvas cap keeps shrinking with the
        // viewport, so the row wrapped onto a second line (rendering above the pill) with real
        // empty space still visible to its right — measured on a 1440px laptop width, nothing to
        // do with actually running out of screen. A mirrored right margin (`100vw - 2 * leftPct`)
        // reproduces the SAME ~55.7% by construction (the bar was authored with symmetric margins)
        // — no wider at all. There is no longer a bar to stay symmetric with, so the right margin
        // only needs to clear the screen edge: a small fixed gutter, not a mirrored one.
        maxWidth: mobile ? undefined : `calc(100vw - ${DESKTOP.leftPct}vw - 24px)`,
        pointerEvents: 'none',
      }}
    >
      <button
        type="button"
        data-testid="view-toggle-pill"
        aria-expanded={open}
        aria-label={open ? 'Hide views' : 'Show views'}
        onClick={onToggleOpen}
        className="reel-touch-target relative bg-reel-chrome-panel backdrop-blur-reel-chrome font-reel-medium text-reel-chrome-textPrimary"
        style={{
          height: unit(BUTTON.h),
          padding: `0 ${unit(BUTTON.padX)}`,
          borderRadius: unit(BUTTON.radius),
          fontSize: unitMin(BUTTON.font, MIN_LABEL_PX),
          lineHeight: BUTTON.lineHeight,
          pointerEvents: 'auto', // the pill is always painted at full opacity (§1.2)
          // Without `relative` the pill has no positioned ancestor, so `.reel-touch-target::after`
          // (index.css) sizes to the outer `position: fixed` widget and swallows taps anywhere in
          // the wrapped row's gaps (review finding). Cap at the pill↔row gutter, same as ViewButton.
          '--hit-h': unit(BUTTON.h + rowGapAuthored),
        }}
      >
        {PILL_LABEL}
      </button>
      {present && (
        <div
          data-testid="view-toggle-row"
          // SPEC-57 fix 4: desktop wraps beside the pill (flex-wrap, D-5). Mobile is a single
          // vertical column below the pill (427:23/166, corrected) — one button per row, never a
          // wrap, so it grows straight down the left edge clear of the centred wordmark.
          className={mobile ? 'flex flex-col items-start' : 'flex flex-wrap items-start'}
          style={{
            gap: unit(mobile ? MOBILE.gapY : DESKTOP.gapX),
            opacity,
            // SPEC-57 fix 4: a tween, not a plain dissolve — the row slides out of/into the pill
            // along the direction it grows away from it (sideways on desktop, downward on mobile),
            // fading at the same time. `opacity` (not `present`) drives it so the two rAFs
            // useFadePresence already inserts for a from-value to ease from are shared, not duplicated.
            transform: opacity
              ? (mobile ? 'translateY(0px)' : 'translateX(0px)')
              : (mobile
                ? `translateY(calc(-1 * ${unit(TWEEN_OFFSET_AUTHORED)}))`
                : `translateX(${unit(TWEEN_OFFSET_AUTHORED)})`),
            transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
            maxWidth: mobile ? undefined : '100%',
          }}
        >
          {buttons.map((button) => (
            <ViewButton key={button.key} button={button} unit={unit} unitMin={unitMin} rowGapAuthored={rowGapAuthored} />
          ))}
        </div>
      )}
    </div>
  );
}

export default ReelViewToggle;
